/**
 * Stage 7c: Final Polish — final editorial + SEO-fix pass before publish.
 *
 * Fixes the SEO issues flagged by stage 6 and lightly improves the article,
 * while preserving all internal links and CTA shortcodes (enforced by code
 * guardrails on the backend). If a polish would drop a link/CTA or worsen SEO,
 * it is rejected and the original article is kept.
 *
 * The panel just runs the stage and surfaces the polish report stored at
 * document.preflight.polish. Editing the article itself is done in the document
 * editor; editing the prompt is done in the Prompt Management editor.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import type { ArticleDocument } from '../types';

type PolishReport = {
  accepted?: boolean;
  rejection_reason?: string;
  before_fails?: number | null;
  after_fails?: number | null;
  changes_summary?: string[];
  flags?: string[];
  regressed_checks?: { check_id?: string; check_name?: string }[];
  missing_links?: string[];
  missing_ctas?: string[];
};

export function renderPolishOutput(doc: ArticleDocument) {
  const polish = (doc.preflight as any)?.polish as PolishReport | undefined;
  if (!polish) {
    return <div className="text-sm text-gray-500">Not run yet.</div>;
  }

  const accepted = polish.accepted;
  const changes = Array.isArray(polish.changes_summary) ? polish.changes_summary : [];
  const missingLinks = Array.isArray(polish.missing_links) ? polish.missing_links : [];
  const missingCtas = Array.isArray(polish.missing_ctas) ? polish.missing_ctas : [];

  return (
    <div className="text-xs text-gray-600 space-y-2">
      <div
        className={`inline-block px-2 py-0.5 rounded font-medium ${
          accepted ? 'bg-green-50 text-green-700 border border-green-200'
                   : 'bg-red-50 text-red-700 border border-red-200'
        }`}
      >
        {accepted ? 'Polish applied' : 'Polish rejected — original kept'}
      </div>

      {!accepted && polish.rejection_reason && (
        <div className="text-red-600">Reason: {polish.rejection_reason}</div>
      )}

      {(polish.before_fails != null || polish.after_fails != null) && (
        <div>
          <span className="text-gray-400">SEO failing checks:</span>{' '}
          <span className="font-medium">{polish.before_fails ?? '?'} → {polish.after_fails ?? '?'}</span>
        </div>
      )}

      {missingLinks.length > 0 && (
        <div className="text-red-600">Would have dropped links: {missingLinks.join(', ')}</div>
      )}
      {missingCtas.length > 0 && (
        <div className="text-red-600">Would have dropped CTAs: {missingCtas.join(', ')}</div>
      )}

      {changes.length > 0 && (
        <div>
          <div className="text-gray-400 mb-1">Changes ({changes.length}):</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {changes.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>
      )}

      {Array.isArray(polish.flags) && polish.flags.length > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 p-2">
          <div className="text-amber-800 font-medium mb-1">
            Flagged for human review ({polish.flags.length}) — issues the editor noticed but is not allowed to fix:
          </div>
          <ul className="list-disc pl-4 space-y-0.5 text-amber-800">
            {polish.flags.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function Stage7cFinalPolishV2() {
  return (
    <StagePanel
      stageId="stage_7c_final_polish"
      title="Stage 7c: Final Polish"
      description="Final editorial + SEO-fix pass before publish. Fixes flagged SEO issues and lightly improves the article. All internal links and CTA shortcodes are preserved by guardrails — if a polish would drop one or worsen SEO, it is rejected and the original is kept."
      isLlmStage
      renderOutput={renderPolishOutput}
    />
  );
}
